import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, ArrowLeft } from "lucide-react";
import slides from "../data/vaterstimmeSlides";

const G = { gold: "#D4AF37", goldSoft: "#EAD08A", deep: "#0B1A36", midnight: "#081328", cream: "#F7EFDB" };

/* Compass-Rose Brand-Mark (klein, Top-Left) */
const CompassMark = ({ size = 32 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden>
    <circle cx="32" cy="32" r="29" fill="none" stroke={G.gold} strokeWidth="1.2" opacity="0.45" />
    <g fill={G.gold}>
      <path d="M32 6 L34.2 30 L32 32 L29.8 30 Z" />
      <path d="M32 58 L34.2 34 L32 32 L29.8 34 Z" opacity="0.7" />
      <path d="M6 32 L30 29.8 L32 32 L30 34.2 Z" opacity="0.7" />
      <path d="M58 32 L34 29.8 L32 32 L34 34.2 Z" opacity="0.7" />
    </g>
    <circle cx="32" cy="32" r="2" fill={G.gold} />
  </svg>
);

/* ── COVER ─────────────────────────────────────────────────────────────── */
const Cover = ({ s }) => (
  <div className="w-full flex flex-col items-center justify-center text-center px-8 lg:px-16 py-12 relative min-h-[600px]">
    <div className="absolute top-8 left-1/2 -translate-x-1/2 opacity-90">
      <CompassMark size={64} />
    </div>
    <div className="mt-16">
      <h1
        className="serif font-light tracking-tight leading-[0.95]"
        style={{ fontSize: "clamp(2.5rem,7vw,6.5rem)" }}
      >
        <span className="text-white">VATER</span>
        <span style={{ color: G.gold }}>STIMME</span>
        <sup className="text-xl md:text-2xl align-super ml-1" style={{ color: G.gold }}>™</sup>
      </h1>
      <div className="mx-auto mt-4 w-32 h-px" style={{ background: `linear-gradient(90deg, transparent, ${G.gold}, transparent)` }} />
    </div>

    <p className="serif italic mt-8 text-xl md:text-2xl text-white/85 max-w-3xl leading-snug">
      {s.subtitle}
    </p>
    {s.intro && (
      <p className="text-white/65 text-base md:text-lg max-w-3xl mt-6 leading-relaxed">{s.intro}</p>
    )}

    {/* 4 Pillars */}
    {s.pillars && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12 max-w-5xl w-full">
        {s.pillars.map((p, i) => (
          <div key={i} className="rounded-2xl p-5 border bg-white/[0.025]" style={{ borderColor: `${G.gold}33` }}>
            <div className="text-[10px] tracking-[0.25em] font-bold mb-2" style={{ color: G.gold }}>
              {p.h}
            </div>
            <div className="text-white/75 text-xs leading-relaxed">{p.t}</div>
          </div>
        ))}
      </div>
    )}

    {s.tagline && (
      <div className="mt-12 text-[11px] md:text-xs tracking-[0.45em] font-semibold text-white/65 uppercase">
        {s.tagline}
      </div>
    )}
    {s.closing && (
      <div className="serif italic mt-3 text-lg md:text-xl" style={{ color: G.gold }}>
        {s.closing}
      </div>
    )}
  </div>
);

/* ── CONTENT ───────────────────────────────────────────────────────────── */
const Content = ({ s }) => {
  const isGrid = s.layout === "grid";
  const isWheel = s.layout === "wheel";
  const isPath = s.layout === "path";
  const dense = isGrid || isWheel || isPath;

  return (
    <div className="w-full px-8 lg:px-14 py-10 lg:py-12">
      {/* Header */}
      <div className="mb-6 lg:mb-8 max-w-5xl">
        {s.kicker && (
          <div className="overline mb-2" style={{ color: G.gold }}>
            {s.kicker}
          </div>
        )}
        <h2
          className="serif font-light leading-tight"
          style={{
            color: G.gold,
            fontSize: dense ? "clamp(1.75rem,2.6vw,2.4rem)" : "clamp(2rem,3.2vw,3rem)",
          }}
        >
          {s.title}
        </h2>
        {s.subtitle && (
          <p className="serif italic text-white/65 text-base lg:text-lg mt-2 max-w-3xl">
            {s.subtitle}
          </p>
        )}
        {s.intro && (
          <p className="text-white/55 text-sm lg:text-base mt-3 max-w-3xl leading-relaxed">
            {s.intro}
          </p>
        )}
      </div>

      <div className={`grid ${dense ? "lg:grid-cols-[1.55fr_1fr]" : "lg:grid-cols-[1fr_1fr]"} gap-8 lg:gap-12`}>
        {/* LEFT — bullets / wheel / list / path */}
        <div>
          {isWheel ? (
            <div className="relative">
              <div className="flex justify-center mb-5">
                <div
                  className="rounded-full px-7 py-4 border-2 text-center"
                  style={{ borderColor: G.gold, background: "rgba(212,175,55,0.08)" }}
                >
                  <div className="text-[10px] tracking-[0.35em] uppercase text-white/55 mb-1">
                    Im Zentrum
                  </div>
                  <div className="serif text-lg lg:text-xl font-light" style={{ color: G.gold }}>
                    {s.centerHub}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {s.bullets?.map((b, i) => (
                  <div
                    key={i}
                    className="rounded-xl border p-4"
                    style={{ borderColor: `${G.gold}33`, background: "rgba(212,175,55,0.025)" }}
                  >
                    <div className="font-semibold tracking-wide uppercase text-[11px] mb-1" style={{ color: G.gold }}>
                      {b.h}
                    </div>
                    <div className="text-white/70 text-xs leading-relaxed">{b.t}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : isPath ? (
            /* 6-step horizontal arrow flow */
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {s.bullets?.map((b, i) => (
                <div key={i} className="relative">
                  <div
                    className="rounded-xl border p-3 h-full"
                    style={{ borderColor: `${G.gold}33`, background: "rgba(212,175,55,0.025)" }}
                  >
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold mb-2 mx-auto"
                      style={{ background: G.gold, color: G.midnight }}
                    >
                      {i + 1}
                    </div>
                    <div className="font-semibold tracking-wide uppercase text-[11px] mb-1 text-center" style={{ color: G.gold }}>
                      {b.h}
                    </div>
                    <div className="text-white/65 text-[10.5px] leading-snug text-center">{b.t}</div>
                  </div>
                  {i < s.bullets.length - 1 && (
                    <div
                      className="hidden lg:block absolute top-1/2 -right-1.5 -translate-y-1/2 text-base"
                      style={{ color: G.gold }}
                    >
                      ›
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : isGrid ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {s.bullets?.map((b, i) => (
                <div
                  key={i}
                  className="rounded-xl border p-4"
                  style={{ borderColor: `${G.gold}33`, background: "rgba(212,175,55,0.025)" }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="shrink-0 w-7 h-7 rounded-full border flex items-center justify-center text-[10px] font-semibold mt-0.5"
                      style={{ borderColor: G.gold, color: G.gold }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold tracking-wide uppercase text-[11px] mb-1" style={{ color: G.gold }}>
                        {b.h}
                      </div>
                      {b.t && <div className="text-white/70 text-xs leading-relaxed">{b.t}</div>}
                      {b.items && (
                        <ul className="space-y-1 mt-1.5">
                          {b.items.map((it, j) => (
                            <li key={j} className="text-white/70 text-xs leading-relaxed flex gap-1.5">
                              <span style={{ color: G.gold }}>·</span>
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ul className="space-y-4">
              {s.bullets?.map((b, i) => (
                <li key={i} className="flex gap-4">
                  <div
                    className="shrink-0 w-8 h-8 rounded-full border flex items-center justify-center text-xs font-semibold mt-0.5"
                    style={{ borderColor: G.gold, color: G.gold }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="font-semibold tracking-wide uppercase text-sm" style={{ color: G.gold }}>
                      {b.h}
                    </div>
                    {b.t && (
                      <div className="text-white/70 text-sm md:text-[15px] leading-relaxed mt-1">
                        {b.t}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Secondary numbered-steps strip */}
          {s.secondary && (
            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.025] p-5">
              <div className="text-[11px] uppercase tracking-[0.35em] font-bold mb-4" style={{ color: G.gold }}>
                {s.secondary.title}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {s.secondary.steps?.map((st, i) => (
                  <div key={i} className="text-center">
                    <div
                      className="mx-auto w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold mb-2"
                      style={{ background: G.gold, color: G.midnight }}
                    >
                      {i + 1}
                    </div>
                    <div className="font-semibold uppercase text-[10px] tracking-wider mb-1" style={{ color: G.gold }}>
                      {st.h}
                    </div>
                    <div className="text-white/65 text-[10px] leading-snug">{st.t}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Checklist block */}
          {s.checklist && (
            <div className="mt-6 rounded-xl border p-5" style={{ borderColor: `${G.gold}55`, background: "rgba(212,175,55,0.04)" }}>
              <div className="text-[11px] uppercase tracking-[0.35em] font-bold mb-4" style={{ color: G.gold }}>
                {s.checklist.title}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {s.checklist.items.map((it, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-white/80 text-xs leading-snug">
                    <div
                      className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center mt-0.5"
                      style={{ borderColor: G.gold }}
                    >
                      <span style={{ color: G.gold, fontSize: "9px" }}>✓</span>
                    </div>
                    <span>{it}</span>
                  </div>
                ))}
              </div>
              {s.checklist.note && (
                <div className="mt-4 text-center serif italic text-white/65 text-sm">
                  &ldquo;{s.checklist.note}&rdquo;
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — sidebar */}
        <div className="flex flex-col gap-4">
          {s.mantra && (
            <div
              className="relative rounded-2xl p-6 border"
              style={{ borderColor: `${G.gold}55`, background: "rgba(212,175,55,0.04)" }}
            >
              <div className="text-2xl absolute top-2 left-4" style={{ color: G.gold }}>
                &ldquo;
              </div>
              <p className="serif italic text-base lg:text-lg text-white/90 leading-snug">
                {s.mantra}
              </p>
              {s.mantraSub && (
                <p className="text-white/55 text-[10px] mt-3 tracking-[0.25em] uppercase font-semibold">
                  {s.mantraSub}
                </p>
              )}
            </div>
          )}

          {s.stats && (
            <div
              className="rounded-2xl border p-5"
              style={{ borderColor: `${G.gold}33`, background: "rgba(212,175,55,0.02)" }}
            >
              <div className="text-[10px] uppercase tracking-[0.35em] font-bold mb-3 text-center" style={{ color: G.gold }}>
                Fakten · Studien
              </div>
              <div className="grid grid-cols-5 gap-2">
                {s.stats.map((st, i) => (
                  <div key={i} className="text-center">
                    <div className="serif text-xl lg:text-2xl font-light" style={{ color: G.gold }}>
                      {st.v}
                    </div>
                    <div className="text-white/55 text-[9px] leading-tight mt-1">{st.l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {s.side && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] font-semibold mb-3" style={{ color: G.gold }}>
                {s.side.title}
              </div>
              <div className="text-white/70 text-xs leading-relaxed space-y-1.5">
                {s.side.items.map((it, i) => (
                  <div key={i}>◆ {it}</div>
                ))}
              </div>
            </div>
          )}

          {s.cta && (
            <div
              className="rounded-2xl p-5 border"
              style={{ borderColor: `${G.gold}77`, background: "rgba(212,175,55,0.07)" }}
            >
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: G.gold }}>
                {s.cta.title}
              </div>
              <div className="text-white/80 text-xs leading-relaxed">{s.cta.sub}</div>
            </div>
          )}

          {s.badge && (
            <div className="rounded-xl border border-white/15 bg-white/[0.04] p-4">
              <div className="text-[10px] tracking-[0.4em] font-bold mb-1" style={{ color: G.gold }}>
                {s.badge.title}
              </div>
              {s.badge.lines?.map((l, i) => (
                <div key={i} className="text-white/65 text-[11px] leading-snug">
                  {l}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom-wide footerStrip */}
      {s.footerStrip && (
        <div className="mt-6 rounded-xl border p-4" style={{ borderColor: `${G.gold}33`, background: "rgba(212,175,55,0.025)" }}>
          {s.footerStripTitle && (
            <div className="text-center text-white/80 text-sm mb-3 serif italic">
              {s.footerStripTitle}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {s.footerStrip.map((f, i) => (
              <div key={i} className="text-center">
                <div className="font-bold uppercase text-[10px] tracking-wider mb-1" style={{ color: G.gold }}>
                  {f.h}
                </div>
                <div className="text-white/65 text-[10px] leading-snug">{f.t}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const render = (s) => (s.type === "cover" ? <Cover s={s} /> : <Content s={s} />);

export default function SatVaterstimmeDeck() {
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);
  const total = slides.length;
  const go = useCallback((d) => setIdx((i) => Math.min(total - 1, Math.max(0, i + d))), [total]);

  useEffect(() => {
    const k = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "f") setFs((f) => !f);
      else if (e.key === "Escape") setFs(false);
      else if (e.key === "Home") setIdx(0);
      else if (e.key === "End") setIdx(total - 1);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go, total]);

  const slide = slides[idx];

  return (
    <div
      className={`${fs ? "fixed inset-0 z-[100]" : "min-h-screen pt-24 pb-12"} bg-[#0B1A36] text-white`}
      data-testid="sat-vaterstimme-deck"
    >
      <div className={`${fs ? "h-screen" : ""} container-sat`}>
        {!fs && (
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/ecosystem/vaterstimme"
              className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase hover:translate-x-[-2px] transition"
              style={{ color: G.gold }}
              data-testid="vaterstimme-deck-back"
            >
              <ArrowLeft size={12} /> Zurück
            </Link>
            <div className="text-[10px] tracking-[0.35em] uppercase text-white/45">
              VATERSTIMME · Initiative der SAT Akademie
            </div>
            <button
              onClick={() => setFs(true)}
              className="text-white/55 hover:text-[#D4AF37] flex items-center gap-2 text-xs uppercase tracking-wider"
              data-testid="vaterstimme-deck-fullscreen-on"
            >
              <Maximize2 size={14} /> Vollbild
            </button>
          </div>
        )}

        <div
          className={`relative ${fs ? "h-full" : "min-h-[680px]"} rounded-2xl overflow-hidden`}
          style={{
            background:
              "linear-gradient(135deg, #0B1A36 0%, #07122A 60%, #050B1B 100%)",
            border: fs ? "none" : "1px solid rgba(212,175,55,0.18)",
          }}
          data-testid={`vaterstimme-slide-${slide.num}`}
        >
          {/* Brand · Top-left */}
          <div className="absolute top-5 left-6 flex items-center gap-3 z-10">
            <CompassMark size={28} />
            <div className="leading-tight">
              <div className="serif text-sm" style={{ color: G.gold }}>
                Vaterstimme
              </div>
              <div className="text-[8px] tracking-[0.3em] uppercase text-white/50">
                SAT Akademie · CH
              </div>
            </div>
          </div>

          {/* Page counter · Top-right */}
          <div
            className="absolute top-5 right-6 text-xs tracking-[0.3em] uppercase font-light z-10"
            style={{ color: G.gold }}
          >
            {String(slide.num).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>

          {/* Slide body */}
          <div className="pt-16 pb-12">{render(slide)}</div>

          {/* Footer */}
          <div className="absolute bottom-3 left-0 right-0 text-center text-[9px] tracking-[0.35em] uppercase text-white/35">
            Vaterstimme — Eine Initiative der SAT Akademie für Transformation · Nenad Vladović
          </div>

          {fs && (
            <button
              onClick={() => setFs(false)}
              className="absolute top-5 right-1/2 translate-x-1/2 text-white/55 hover:text-[#D4AF37] flex items-center gap-2 text-xs uppercase tracking-wider z-20"
              data-testid="vaterstimme-deck-fullscreen-off"
            >
              <Minimize2 size={14} /> Schließen (Esc)
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="mt-5 flex items-center justify-between">
          <button
            onClick={() => go(-1)}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition disabled:opacity-30"
            data-testid="vaterstimme-deck-prev"
          >
            <ChevronLeft size={16} /> Vorherige
          </button>
          <div className="hidden md:flex items-center gap-1.5 max-w-2xl overflow-x-auto">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`shrink-0 transition-all rounded-full ${
                  i === idx ? "w-6 h-2 bg-[#D4AF37]" : "w-2 h-2 bg-white/20 hover:bg-white/40"
                }`}
                aria-label={`Slide ${i + 1}`}
                data-testid={`vaterstimme-deck-dot-${i}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            disabled={idx === total - 1}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-30"
            style={{ background: G.gold, color: G.midnight }}
            data-testid="vaterstimme-deck-next"
          >
            Nächste <ChevronRight size={16} />
          </button>
        </div>

        {!fs && (
          <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase text-white/30">
            Pfeiltasten ◄ ► · Leertaste · F für Vollbild · Esc zum Schließen
          </div>
        )}
      </div>
    </div>
  );
}
